import React, { useCallback, useEffect, useRef, useState } from 'react';
import SplashScreen from './SplashScreen';
import {
  SPLASH_VARIANT_OPTIONS,
  readSplashVariant,
  readSplashSoundEnabled,
  writeSplashVariant,
  writeSplashSoundEnabled,
} from './splashConstants';
import { playSplashSting } from './splashSound';
import './splash.css';

const THUMB_COLORS = {
  blox: 'radial-gradient(circle at 70% 30%, rgba(229,9,20,0.5), transparent 55%), linear-gradient(145deg, #12080f, #0a0a12)',
  particles: 'radial-gradient(circle at 30% 40%, #f5c518, transparent 50%), #0a0a12',
  retro: 'repeating-linear-gradient(0deg, #0a1a14, #0a1a14 4px, #0d221c 4px, #0d221c 8px)',
  cinematic: 'radial-gradient(ellipse at 50% 30%, #2a1830, #0a0a0f)',
};

/**
 * Dev / settings flow: pick intro variant. Open with ?splashPreview=1
 * @param {{ onDone?: () => void }} props
 */
export default function SplashPreviewPicker({ onDone }) {
  const [selected, setSelected] = useState(() => readSplashVariant());
  const [soundOn, setSoundOn] = useState(() => readSplashSoundEnabled());
  const [previewing, setPreviewing] = useState(null);
  const firstCardRef = useRef(null);

  useEffect(() => {
    firstCardRef.current?.focus();
  }, []);

  const persist = useCallback((id) => {
    writeSplashVariant(id);
    setSelected(id);
  }, []);

  const finish = useCallback(() => {
    writeSplashVariant(selected);
    writeSplashSoundEnabled(soundOn);
    if (onDone) onDone();
    else {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('splashPreview');
        window.location.replace(url.pathname + url.search + url.hash);
      } catch {
        window.location.href = '/';
      }
    }
  }, [selected, soundOn, onDone]);

  const playSelected = useCallback(() => {
    setPreviewing(selected);
  }, [selected]);

  if (previewing) {
    return (
      <SplashScreen
        variant={previewing}
        playSound={soundOn}
        onComplete={() => setPreviewing(null)}
      />
    );
  }

  return (
    <div className="zflix-splash-preview" role="dialog" aria-modal="true" aria-label="Choose your intro">
      <h1>Choose your intro</h1>
      <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, maxWidth: 520 }}>
        <strong>Blox bounce</strong> is the production intro. Other styles are optional alternates, saved on this device as{' '}
        <code style={{ fontSize: 12 }}>zflix_splash_variant</code>. Dev URL:{' '}
        <code style={{ fontSize: 12 }}>?splashPreview=1</code>
      </p>

      <div className="zflix-splash-preview-grid" role="listbox" aria-label="Intro styles">
        {SPLASH_VARIANT_OPTIONS.map((opt, idx) => (
          <button
            key={opt.id}
            type="button"
            ref={idx === 0 ? firstCardRef : undefined}
            role="option"
            aria-selected={selected === opt.id}
            data-selected={selected === opt.id ? 'true' : 'false'}
            className="zflix-splash-preview-card"
            onClick={() => persist(opt.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                persist(opt.id);
              }
            }}
          >
            <div
              className="zflix-splash-preview-thumb"
              style={{ background: THUMB_COLORS[opt.id] || '#0d0d14' }}
            >
              <span style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: 3, textShadow: '0 2px 8px #000' }}>
                Z
              </span>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{opt.label}</div>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, lineHeight: 1.4 }}>{opt.blurb}</p>
            </div>
          </button>
        ))}
      </div>

      <label
        style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, fontSize: 14, cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={soundOn}
          onChange={(e) => {
            const on = e.target.checked;
            setSoundOn(on);
            writeSplashSoundEnabled(on);
            if (on) playSplashSting();
          }}
        />
        Play open sound (original sting)
      </label>

      <div className="zflix-splash-preview-actions">
        <button type="button" className="zflix-splash-preview-primary" onClick={playSelected}>
          Preview selected
        </button>
        <button type="button" className="zflix-splash-preview-primary" onClick={finish}>
          Save &amp; open ZFLIX
        </button>
        <button
          type="button"
          className="zflix-splash-preview-ghost"
          onClick={() => {
            writeSplashSoundEnabled(soundOn);
            if (onDone) onDone();
          }}
        >
          Skip preview
        </button>
      </div>
    </div>
  );
}
